import { UpperCasePipe } from '@angular/common';
import type { ElementRef } from '@angular/core';
import { Component, computed, effect, input, output, signal, viewChild } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';

import { ENotificationCardType } from '@rpgc-ai/shared-interfaces/interfaces/notifications.interfaces';

import { TranslocoPipe } from '@jsverse/transloco';
import { EMPTY, interval, map, of, switchMap } from 'rxjs';

import { SwipeDirective } from '../swipe/swipe.directive';

const CARD_TYPE_ICON: Record<ENotificationCardType, string> = {
  [ENotificationCardType.SUCCESS]: 'ph ph-check-circle',
  [ENotificationCardType.ERROR]: 'ph ph-x-circle',
  [ENotificationCardType.INFO]: 'ph ph-warning-circle rotate-180',
  [ENotificationCardType.WARNING]: 'ph ph-warning-circle',
};

export enum ESwipeDirection {
  Left = 'left',
  Right = 'right',
}

export const SWIPE_LEFT = '-150%';
export const SWIPE_RIGHT = '150%';

@Component({
  imports: [SwipeDirective, TranslocoPipe, UpperCasePipe],
  selector: 'app-ui-notification-card',
  styleUrl: './notification-card.component.scss',
  templateUrl: './notification-card.component.html',
})
export class NotificationCardComponent {
  readonly wrapper = viewChild<ElementRef<HTMLElement>>('wrapper');

  readonly type = input.required<ENotificationCardType>();
  readonly text = input.required<string>();
  readonly id = input.required<string>();

  readonly lifeTime = input<number>(5000);
  private readonly startTime = Date.now();

  readonly paused = signal(false);
  readonly consumed = signal(0);

  private lastTick = signal(Date.now());
  protected readonly ESwipeDirection = ESwipeDirection;

  readonly lifeTimeProgress = toSignal(
    interval(10).pipe(
      switchMap(() => (this.paused() ? EMPTY : of(null))),
      map(() => {
        const now = Date.now();
        const elapsed = now - this.lastTick();
        this.lastTick.set(now);

        this.consumed.update((v) => v + elapsed);

        const total = this.lifeTime();
        const remaining = Math.max(0, 100 - (this.consumed() / total) * 100);

        return Math.floor(remaining);
      }),
    ),
    { initialValue: 100 },
  );

  readonly closeEvent = output<string>();

  readonly icon = computed<string>(() => CARD_TYPE_ICON[this.type()]);

  constructor() {
    this.#closeOnTimerOutEffect();
  }

  onPause(): void {
    this.paused.set(true);
  }

  onResume(): void {
    this.lastTick.set(Date.now());
    this.paused.set(false);
  }

  /**
   * Закрытие уведомления
   */
  close(): void {
    this.closeEvent.emit(this.id());
  }

  onSwipe(direction: ESwipeDirection): void {
    const wrapper = this.wrapper();
    if (!wrapper) {
      return;
    }

    const element = wrapper.nativeElement;
    const distance = direction === ESwipeDirection.Left ? SWIPE_LEFT : SWIPE_RIGHT;
    element.style.transform = `translateX(${distance})`;
    element.style.transition = 'transform 0.4s ease-out';

    setTimeout(() => {
      this.close();
    }, 400);
  }

  #closeOnTimerOutEffect(): void {
    effect(() => {
      if (this.lifeTimeProgress() === 0) {
        this.close();
      }
    });
  }
}
